import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tag, Select, Typography, Tooltip, Tabs, Divider, Alert, DatePicker, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, UserAddOutlined, KeyOutlined, CopyOutlined } from '@ant-design/icons';
import { tenantService, Tenant, CreateTenantRequest, UpdateTenantRequest } from '../services/tenantService';
import { apiKeyService, ApiKey, CreateApiKeyRequest, UpdateApiKeyRequest } from '../services/apiKeyService';
import TenantInitWizard from './TenantInitWizard';
import CreatingTenantsManager from './CreatingTenantsManager';
import { useI18n } from '../hooks/useI18n';
import TimeDisplay from './TimeDisplay';
import './common.css';
import dayjs from 'dayjs';

const { Title } = Typography;

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [activeTab, setActiveTab] = useState('tenant-list');
  const [form] = Form.useForm();
  const [apiKeyForm] = Form.useForm();
  const { t } = useI18n();
  
  // API Key 相关状态
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      setTenants(data);
    } catch (error: any) {
      message.error(t('tenant.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values: CreateTenantRequest) => {
    try {
      if (editingTenant) {
        await tenantService.updateTenant(editingTenant.id, values as UpdateTenantRequest);
        message.success(t('tenant.updateSuccess'));
      } else {
        await tenantService.createTenant(values);
        message.success(t('tenant.createSuccess'));
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTenant(null);
      loadTenants();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('tenant.operationFailed'));
      console.error(error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await tenantService.deleteTenant(id);
      message.success(t('tenant.deleteSuccess'));
      loadTenants();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('common.error'));
    }
  };

  const openModal = async (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      form.setFieldsValue({
        code: tenant.code,
        name: tenant.name,
        status: tenant.status || 'active',
        language: tenant.language || 'zh-CN',
        timezone: tenant.timezone || 'Asia/Shanghai',
        theme: tenant.theme || 'light',
        eroad_tenant_id: (tenant as any).eroad_tenant_id || undefined
      });
      // 加载该租户的 API Keys
      await loadApiKeys(tenant.code);
    } else {
      setEditingTenant(null);
      form.resetFields();
      setApiKeys([]);
    }
    setModalVisible(true);
  };

  const loadApiKeys = async (tenantCode: string) => {
    try {
      setApiKeyLoading(true);
      const data = await apiKeyService.getApiKeys(tenantCode);
      setApiKeys(data);
    } catch (error: any) {
      message.error('加载 API Keys 失败');
      console.error(error);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingTenant(null);
    setApiKeys([]);
    setNewApiKey(null);
    setShowApiKey({});
  };

  const handleCreateApiKey = async (values: CreateApiKeyRequest) => {
    if (!editingTenant) return;
    
    try {
      const result = await apiKeyService.createApiKey({
        ...values,
        tenant_code: editingTenant.code
      });
      setNewApiKey(result.api_key);
      message.success('API Key 创建成功，请妥善保存！');
      await loadApiKeys(editingTenant.code);
      apiKeyForm.resetFields();
      setApiKeyModalVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建 API Key 失败');
      console.error(error);
    }
  };

  const handleUpdateApiKey = async (apiKeyId: string, updates: UpdateApiKeyRequest) => {
    try {
      await apiKeyService.updateApiKey(apiKeyId, updates);
      message.success('API Key 更新成功');
      if (editingTenant) {
        await loadApiKeys(editingTenant.code);
      }
      setEditingApiKey(null);
      apiKeyForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || '更新 API Key 失败');
      console.error(error);
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    try {
      await apiKeyService.deleteApiKey(apiKeyId);
      message.success('API Key 删除成功');
      if (editingTenant) {
        await loadApiKeys(editingTenant.code);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除 API Key 失败');
      console.error(error);
    }
  };

  const openApiKeyModal = (apiKey?: ApiKey) => {
    if (apiKey) {
      setEditingApiKey(apiKey);
      apiKeyForm.setFieldsValue({
        name: apiKey.name,
        description: apiKey.description,
        is_active: apiKey.is_active,
        expires_at: apiKey.expires_at ? dayjs(apiKey.expires_at) : undefined
      });
    } else {
      setEditingApiKey(null);
      apiKeyForm.resetFields();
      setNewApiKey(null);
    }
    setApiKeyModalVisible(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const tenantColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('tenant.code'),
      dataIndex: 'code',
      key: 'code',
      width: 150,
      ellipsis: true,
    },
    {
      title: t('tenant.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('tenant.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const color = status === 'active' ? 'green' : status === 'inactive' ? 'orange' : 'red';
        return <Tag color={color}>
          {status === 'active' ? t('tenant.active') :
           status === 'inactive' ? t('tenant.inactive') : status}
        </Tag>;
      },
    },
    {
      title: t('tenant.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: t('tenant.updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: t('common.edit'),
      key: 'actions',
      width: 150,
      render: (record: Tenant) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            {t('tenant.edit')}
          </Button>
          <Popconfirm
            title={t('tenant.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              {t('tenant.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'tenant-list',
      label: t('tenant.list'),
      icon: <EditOutlined />,
      children: (
        <div className="page-content">
                  <div className="table-header">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            {t('tenant.create')}
          </Button>
        </div>

          <Table
            columns={tenantColumns}
            dataSource={tenants}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`
            }}
            scroll={{ 
              x: 800
            }}
            tableLayout="fixed"
            style={{ 
              maxWidth: '100%'
            }}
          />
        </div>
      )
    },
    {
      key: 'tenant-init',
      label: t('tenant.initWizard'),
      icon: <UserAddOutlined />,
      children: <TenantInitWizard />
    },
    {
      key: 'creating-tenants',
      label: t('tenant.creatingManager'),
      icon: <ClockCircleOutlined />,
      children: <CreatingTenantsManager />
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <Title level={2}>{t('menu.tenants')}</Title>
        <p>{t('tenant.descriptionText')}</p>
      </div>

      <Tabs 
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{ marginTop: '20px' }}
      />

      {/* Tenant Creation/Edit Modal */}
      <Modal
        title={editingTenant ? t('tenant.edit') : t('tenant.create')}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={900}
      >
        <Tabs
          items={[
            {
              key: 'tenant-info',
              label: t('tenant.tenantInfo'),
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleCreateOrUpdate}
                >
                  <Form.Item
                    name="code"
                    label={t('tenant.code')}
                    rules={[
                      { required: true, message: t('tenant.codeRequired') },
                      { pattern: /^[a-zA-Z0-9_-]+$/, message: t('tenant.codePattern') }
                    ]}
                  >
                    <Input
                      placeholder={t('tenant.codePlaceholder')}
                      disabled={!!editingTenant}
                    />
                  </Form.Item>

                  <Form.Item
                    name="name"
                    label={t('tenant.name')}
                    rules={[{ required: true, message: t('tenant.nameRequired') }]}
                  >
                    <Input placeholder={t('tenant.namePlaceholder')} />
                  </Form.Item>

                  <Form.Item
                    name="status"
                    label={t('tenant.status')}
                    initialValue="active"
                  >
                    <Select>
                      <Select.Option value="active">{t('tenant.active')}</Select.Option>
                      <Select.Option value="inactive">{t('tenant.inactive')}</Select.Option>
                      <Select.Option value="suspended">{t('tenant.suspended')}</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="language"
                    label={t('tenant.language')}
                    initialValue="zh-CN"
                  >
                    <Select>
                      <Select.Option value="zh-CN">简体中文</Select.Option>
                      <Select.Option value="en-US">English</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="timezone"
                    label={t('tenant.timezone')}
                    initialValue="Asia/Shanghai"
                  >
                    <Select>
                      <Select.Option value="Asia/Shanghai">Asia/Shanghai (UTC+8)</Select.Option>
                      <Select.Option value="America/New_York">America/New_York (UTC-5)</Select.Option>
                      <Select.Option value="Europe/London">Europe/London (UTC+0)</Select.Option>
                      <Select.Option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</Select.Option>
                      <Select.Option value="Australia/Sydney">Australia/Sydney (UTC+10)</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="theme"
                    label={t('tenant.theme')}
                    initialValue="light"
                  >
                    <Select>
                      <Select.Option value="light">{t('tenant.themeLight')}</Select.Option>
                      <Select.Option value="dark">{t('tenant.themeDark')}</Select.Option>
                      <Select.Option value="auto">{t('tenant.themeAuto')}</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="eroad_tenant_id"
                    label="EROAD Tenant ID / 易路租户ID"
                  >
                    <Input placeholder="EROAD Tenant ID / 易路租户ID" />
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit">
                        {editingTenant ? t('common.save') : t('tenant.create')}
                      </Button>
                      <Button onClick={closeModal}>
                        {t('common.cancel')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )
            },
            ...(editingTenant ? [{
              key: 'api-keys',
              label: (
                <span>
                  <KeyOutlined /> API Keys
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>管理该租户的 API Keys，用于系统间调用</span>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => openApiKeyModal()}
                    >
                      创建 API Key
                    </Button>
                  </div>
                  
                  <Table
                    columns={[
                      {
                        title: '名称',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: '描述',
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                      },
                      {
                        title: '状态',
                        dataIndex: 'is_active',
                        key: 'is_active',
                        render: (isActive: boolean) => (
                          <Tag color={isActive ? 'green' : 'red'}>
                            {isActive ? '活跃' : '已禁用'}
                          </Tag>
                        ),
                      },
                      {
                        title: '过期时间',
                        dataIndex: 'expires_at',
                        key: 'expires_at',
                        render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '永不过期',
                      },
                      {
                        title: '最后使用',
                        dataIndex: 'last_used_at',
                        key: 'last_used_at',
                        render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '从未使用',
                      },
                      {
                        title: '创建时间',
                        dataIndex: 'created_at',
                        key: 'created_at',
                        render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
                      },
                      {
                        title: '操作',
                        key: 'actions',
                        render: (record: ApiKey) => (
                          <Space>
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => openApiKeyModal(record)}
                            >
                              编辑
                            </Button>
                            <Popconfirm
                              title="确定要删除这个 API Key 吗？"
                              onConfirm={() => handleDeleteApiKey(record.api_key_id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                              >
                                删除
                              </Button>
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={apiKeys}
                    rowKey="api_key_id"
                    loading={apiKeyLoading}
                    pagination={false}
                    size="small"
                  />
                </div>
              )
            }] : [])
          ]}
        />
      </Modal>

      {/* API Key Create/Edit Modal */}
      <Modal
        title={editingApiKey ? '编辑 API Key' : '创建 API Key'}
        open={apiKeyModalVisible}
        onCancel={() => {
          setApiKeyModalVisible(false);
          setEditingApiKey(null);
          setNewApiKey(null);
          apiKeyForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {newApiKey && (
          <Alert
            message="API Key 创建成功"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>请立即复制并妥善保存此 API Key，它只会显示一次：</p>
                <Input.Group compact>
                  <Input
                    value={newApiKey}
                    readOnly
                    style={{ width: 'calc(100% - 100px)' }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(newApiKey)}
                  >
                    复制
                  </Button>
                </Input.Group>
                <p style={{ marginTop: 8, color: 'red' }}>
                  ⚠️ 如果丢失，需要重新创建新的 API Key
                </p>
              </div>
            }
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        <Form
          form={apiKeyForm}
          layout="vertical"
          onFinish={(values: any) => {
            if (editingApiKey) {
              handleUpdateApiKey(editingApiKey.api_key_id, {
                name: values.name,
                description: values.description,
                is_active: values.is_active,
                expires_at: values.expires_at ? dayjs(values.expires_at).toISOString() : undefined
              });
            } else {
              if (editingTenant) {
                handleCreateApiKey({
                  tenant_code: editingTenant.code,
                  name: values.name,
                  description: values.description,
                  expires_at: values.expires_at ? dayjs(values.expires_at).toISOString() : undefined
                });
              }
            }
          }}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="例如：邮件服务-生产环境" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              placeholder="API Key 的用途说明"
              rows={3}
            />
          </Form.Item>

          {editingApiKey && (
            <Form.Item
              name="is_active"
              label="状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="活跃" unCheckedChildren="已禁用" />
            </Form.Item>
          )}

          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择过期时间（留空则永不过期）"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingApiKey ? '保存' : '创建'}
              </Button>
              <Button onClick={() => {
                setApiKeyModalVisible(false);
                setEditingApiKey(null);
                setNewApiKey(null);
                apiKeyForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TenantManagement;
